/** @jsx jsx */

import { isEmpty, camelCase } from 'lodash';
import { Icon } from '@zapier/zinnia-icons';
import { css, jsx } from '@emotion/core';
import { ChangeEvent, FocusEvent, useState } from 'react';

import { Button, Margin, TextInput } from '@zapier/design-system';
import FieldMessage, {
  FieldType,
  FieldErrorMessage,
} from 'app/developer-v3/components/FieldMessage';
import { getBorderColor, prettyFormatJsonString } from 'app/developer-v3/utils';

import type { DeepLinkParams } from 'app/developer-v3/types';

const Styles = {
  inputCss: (inputBorderColor: string) => [
    inputBorderColor &&
      css`
        border: 1px solid ${inputBorderColor};
        border-radius: 10px;
      `,
  ],
};

type Props = {
  onChange: (
    value: string,
    name: string,
    didConfirm: boolean,
    isValid?: boolean
  ) => void;
  testRequestOutput: {} | Object[];
  value: string;
  meta?: {
    info?: DeepLinkParams;
  };
};

const SampleOutputEditor = ({
  onChange,
  testRequestOutput,
  value,
  meta,
}: Props) => {
  const [isValidJson, setIsValidJson] = useState(true);

  const validateSample = (sample: string) => {
    try {
      JSON.parse(sample);
      setIsValidJson(true);
      onChange(sample, '', true, true);
    } catch (_) {
      setIsValidJson(false);
      onChange(sample, '', true, false);
    }
  };

  const onClick = () => {
    if (isEmpty(testRequestOutput)) {
      return;
    }
    const sampleText = prettyFormatJsonString(
      Array.isArray(testRequestOutput)
        ? testRequestOutput[0]
        : testRequestOutput
    );

    validateSample(sampleText);
  };

  const info = meta?.info || {};
  const inputBorderColor = getBorderColor(info.type);

  return (
    <Margin margin="20px 0px 10px">
      {!isEmpty(testRequestOutput) && (
        <Margin margin="0px 0px 10px">
          <Button variant="secondary" onPress={onClick} iconPosition="start">
            <Icon name="actionCopy" />
            Use Response from Test Data
          </Button>
        </Margin>
      )}
      <div css={Styles.inputCss(inputBorderColor)}>
        <TextInput
          aria-label="Sample Output Editor"
          isMultiline={true}
          onBlur={(e: FocusEvent<HTMLInputElement>) =>
            validateSample(e.target.value)
          }
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange(e.target.value, '', false)
          }
          rows={10}
          value={value}
        />
      </div>
      {info && (
        <Margin margin="0px 0px 15px">
          <FieldMessage
            fieldKey="sample-output-editor"
            message={info.message}
            type={camelCase(`info ${info.type}`) as FieldType}
          />
        </Margin>
      )}
      {!isValidJson && (
        <aside>
          <FieldErrorMessage message={'Must provide valid JSON.'} />
        </aside>
      )}
    </Margin>
  );
};

export default SampleOutputEditor;
